import { ForecastType, getFocusWorkoutName, type IntervalData, type MagicBucketsData, type MagicBucketsRecord, type MagicBucketsSummary, type Signature } from './../Common/constants';
import { format, parseISO, isValid, parse, closestTo, startOfDay, endOfDay, fromUnixTime, getUnixTime, addMinutes, getDay, intervalToDuration, formatDuration } from 'date-fns';
import route from '../Util/routes';
import { quadInOut } from 'svelte/easing';
import { getLibxert, getWorker } from '@/xert-wasm';
import { type Availability, type ChallengePlan, type ContinuousPlan, type EventPlan, type GoalPlan, type BbpPlan, ProgramType, type RacePlan, SpecificityTypes, defaultChallengePlan, defaultContinuousPlan, defaultEventPlan, defaultForecastOptions, defaultGoalPlan, defaultBbpPlan, defaultRacePlan, defaultTrainingPlanSettings, defaultUnsetAvailability, type TabNames, type CalendarDefaults, type ShortWeekDayProperties, type OverrideAvailabilities, type CalendarSettings, CONSTRAINT_EPSILON } from '@/Common/constants';
import { fade } from 'svelte/transition'
import { get, writable } from 'svelte/store'
import { LocalDate } from '@/time'
import { Activity, type Exercise } from '@/Common/ActivityFunctions';

//@ts-ignore
const Xert = window.Xert;
//@ts-ignore
const jq = window.$

export const useImperialUnits = (): boolean => Xert?._displayUnits === 'imperial';

export function kmToMile(metric: number) {
  return metric * 0.621371;
}
export function milesToKm(miles: number) {
  return miles / 0.621371;
}
export function userDistanceUnit() {
  return useImperialUnits() ? "mi" : "km"
}

export function kmToUserDistanceUnits(metric: number) {
  return useImperialUnits() ? metric * 0.621371 : metric;
}
export function userDistanceUnitsToKm(distance: number) {
  return useImperialUnits() ? distance / 0.621371 : distance;
}

export function meterToFoot(metric: number) {
  return metric * 3.28084;
}

export function kgToLb(metric: number) {
  return metric * 2.2046226218;
}

export function lbToKg(imperial: number) {
  return imperial / 2.2046226218;
}

export function tidy(number) {
    return Math.round(10 * Math.abs(number)) / 10;
}

export function parseDateAndTime(date_string, time_string) {
  let dateAndTime = new Date('');
  let date = parseISO(date_string);
  if (!isValid(date)) return date;

  let timeFormats = [
    'HH', // 24 hour;
    'HH:mm',
    'hha',
    'hh:mma',
  ]
  time_string = time_string.replace(/\s/g, ''); // Remove whitespace with regex
  timeFormats.every(fmt=>{
    dateAndTime = parse(time_string, fmt, date);
    return !isValid(dateAndTime);
  });
  return dateAndTime;
}

export function durationToHoursMinutes(minutes: number){
  const start = new Date(0)
  const end = addMinutes(start, minutes)
  return formatDuration(intervalToDuration({start: start, end: end}), {format: ['hours', 'minutes']})
}

//Takes hours on 24hr time, and converts it to a string that can be set as the value of html time input fields
export function toTimeInputStringHours(hours){
  let displayHours = Math.floor(hours);
  let minutes = Math.floor((hours-displayHours)*60);
  return `${displayHours > 9 ? displayHours : `0${displayHours}`}:${minutes > 9 ? minutes : `0${minutes}`}`;
}

export function toTimeInputString(date){
  let hours = date.getHours();
  let minutes = date.getMinutes();
  return `${hours > 9 ? hours : `0${hours}`}:${minutes > 9 ? minutes : `0${minutes}`}`;
}


export function combineDateTime(date, time){
  time = time.split(":");
  return new Date(`${date}T${time[0]}:${time[1]}:00`);
}

export function toDateInputString(date){
  let month = date.getMonth()+1;
  let day = date.getDate();
  return `${date.getFullYear()}-${month < 10 ? `0${month}` : month}-${day < 10 ? `0${day}` : day}`;
}

export function makeDate(d){
  //The time we get from the weather API has already been adjusted for the timezone, but the date constructor expects utc milliseconds, so we remove timezone offset and multiply
  let date = new Date();
  const timezone = date.getTimezoneOffset()*60;
  date = new Date((d+timezone)*1000);
  return date;
}

export function round(value:number, precision:number = 0){
  return Math.round((value + Number.EPSILON) * Math.pow(10, precision)) /Math.pow(10, precision);
}

/* returns a object with only the true flags to allow the php server to use the isset() to determine true or false flags */
export function getFlags(options){
  return Object.fromEntries(Object.entries(options).filter(([k]) => options[k]))
}

// Shows the messages of a settings response. Returns whether it was taken: false when one of its results says it wasn't (a
// result false with a message, which is shown as a warning; a result false without one only means there was nothing to do).
function displaySettingsResponse(data, callbackFunction:(any)=>any): boolean {
  Object.values(data).forEach((val: any) => {
    if (val?.msg) {
      val?.result? Xert.success(val.msg) : Xert.warning(val.msg);
    }
    
    callbackFunction(val);
    
  });
  if(Object.values(data).find((val: any) => val?.msg) === undefined){
    Xert.success("Updating Settings");
  }
  return !Object.values(data).some((val: any) => val?.result === false && val?.msg);
}

export function createSettingsSubmitHandler(url, getBody:(...args:any)=>any, callbackFunction: (any)=>any = ()=>{}){
  return async (...args:any)=>{
    return window.fetch(url, {
      ...getRequest(),
      method: 'post',
      body: JSON.stringify(getBody(...args)),
    }).then(async (res) => {
      // An HTTP error isn't taken, whatever its body (a 419's JSON holds only a message)
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        Xert.warning(res.status == 419 ? 'Your session has expired. Please reload the page and try again.' : data?.msg ?? 'Settings could not be saved.');
        return false;
      }
      return res.json().then(data=>displaySettingsResponse(data, callbackFunction));
    })
  }
}

/**
 * A settings section the page saves with its Save All: only what has been edited is sent, nothing while it is as last saved.
 * saved holds the body the server has; markSaved() takes it again when the server's values are loaded into the section, so a
 * reload is not taken for an edit. A section shows unsaved edits with `$: changed = section.isChanged($saved, ...the values
 * getBody reads)`, the values listed so Svelte reruns it when they change. A save that fails or is turned down stays to be saved.
 */
export function createSectionSave(url: string, getBody: () => any, failedMessage = 'Settings could not be saved.') {
  const saved = writable('')
  const submitHandler = createSettingsSubmitHandler(url, getBody)
  return {
    saved,
    markSaved: () => saved.set(JSON.stringify(getBody())),
    isChanged: (savedBody: string, ..._values: unknown[]) => savedBody !== '' && JSON.stringify(getBody()) !== savedBody,
    async submit() {
      const body = JSON.stringify(getBody())
      if (body === get(saved)) return
      try {
        if (await submitHandler() !== false) saved.set(body)
      } catch (e) {
        console.error(e)
        Xert.warning(failedMessage)
      }
    },
  }
}

export function isUserInSession(session): boolean {
  if (session == null || session.members == null || session.members.length == 0) return false;
  // TODO: Don't use Xert._username?
  return session.members.map(v=>v.username).indexOf(Xert._username)!=-1
}

export function copyInstance(instance_to_copy) {
  if (instance_to_copy?.session_id?.length > 0) {
    return window.fetch(route('SessionsController@postCopySessionInstance', [instance_to_copy.session_id]), {
      ...getRequest(),
      method: 'post',
    })
    .then(response => response.json())
    .then(data => {
      if (data?.session_id?.length > 0) {
        window.open(route('SessionsController@getSessionScheduler', [data.session_id]), '_blank');
      }
      return data;
    });
  } else {
    return Promise.reject('Invalid instance id.');
  }
}

export function copyTemplate(template_to_copy) {
  if (template_to_copy?.template_id?.length > 0) {
    return window.fetch(route('SessionsController@postCopySessionTemplate', [template_to_copy.template_id]), {
      ...getRequest(),
      method: 'post',
    })
    .then(response => response.json())
    .then(data => {
      if (data?.template_id?.length > 0) {
        window.open(route('SessionsController@getSessionEditor', [data.template_id]), '_blank');
      }
      return data;
    });
  } else {
    return Promise.reject('Invalid template id.');
  }
}

export function playSessionNow(template: any = null, force = false) {
  //if(!template) template = findSessionTemplate(template_id);
  const data = {
    ...template,
    is_group_session: false,
    template_id: template.template_id,
    check_schedule_conflict: true, // check for schedule conflicts.
    resolve_conflicts: force,
  };
  return window.fetch(route('SessionsController@postSessionInstance'), {
    ...getRequest(),
    method: 'post',
    body: JSON.stringify(data),
  })
  .then(response => response.json())
  .then(data=>{
    if (data.error) {
      throw data;
    }
    return data;
  });
}

export async function unscheduleSession(session_id){
  return deleteSession(session_id, false);
}

export async function deleteSession(session_id, isTemplate) {
  if(isTemplate){
    window.fetch(route('SessionsController@deleteSessionTemplate', [session_id]), {
      ...getRequest(),
      method: 'delete',
    })
    .then(response => response.json());
  } else {
    window.fetch(route('SessionsController@deleteSessionInstance', [session_id]), {
      ...getRequest(),
      method: 'delete',
    })
    .then(response => response.json());
  }
}



export function containsQueryRT(exercise, queryString){
  //same as containsQuery but does not search for description, as it does not show up for RT
  if(exercise.name != null && exercise.name.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.tags != null && exercise.tags.filter(exercise => exercise.toLowerCase().includes(queryString.toLowerCase())).length > 0) return true;
  if(exercise.suitability != null && exercise.suitability.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.focus != null && exercise.focus.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.fa != null && exercise.fa.toLowerCase().includes(queryString.toLowerCase())) return true;
  //Focus is sometimes saved as fa, other times saved as focus
  return false;
}

export function containsQuery(exercise, queryString){
  if(exercise.name != null && exercise.name.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.description != null && exercise.description.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.tags != null && exercise.tags.filter(exercise => exercise.toLowerCase().includes(queryString.toLowerCase())).length > 0) return true;
  if(exercise.suitability != null && exercise.suitability.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.focus != null && exercise.focus.toLowerCase().includes(queryString.toLowerCase())) return true;
  if(exercise.fa != null && exercise.fa.toLowerCase().includes(queryString.toLowerCase())) return true;
  //Focus is sometimes saved as fa, other times saved as focus
  return false;
}

export function getDiamondFromDifficulty(difficulty) {
  if (difficulty <= 45) return 1;
  else if (difficulty <= 50) return 1.5;
  else if (difficulty <= 62) return 2;
  else if (difficulty <= 75) return 2.5;
  else if (difficulty <= 90) return 3;
  else if (difficulty <= 110) return 3.5;
  else if (difficulty <= 130) return 4;
  else if (difficulty <= 150) return 4.5;
  else return 5;
}

// Unused for now
export function getDifficultyFromDiamond(diamonds) {
  switch(diamonds) {
    case 1: return 45;
    case 1.5: return 50;
    case 2: return 62;
    case 2.5: return 75;
    case 3: return 90;
    case 3.5: return 110;
    case 4: return 130;
    case 4.5: return 150;
    case 5: return 200;
  }
}

export function preventDefault(e) {
  e.preventDefault();
}

let csrf_token = null
/**
 * Puts focus on the open dialog's main button (marked `data-main-button`) once it has opened. Its focus trap would start on
 * the first focusable element instead, which can be a link the browser then rings, as if reached by keyboard.
 */
export function focusMainButton() {
  setTimeout(() => {
    const buttons = document.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"] [data-main-button]')
    buttons[buttons.length - 1]?.focus()
  }, 50)
}

export function getRequest(): RequestInit {
  let request = baseRequest()
  request.headers['Content-Type'] = 'application/json'
  return request
}

export function baseRequest(): RequestInit {
  if (csrf_token == null) 
    // @ts-ignore
    csrf_token = document.getElementsByName('_token')[0].value;
  return {
    credentials: 'same-origin', // Needed for older browsers, otherwise defaults to 'omit'
    
    // For most of our GET requests, we want an up-to-date version of the resource, for example if you just uploaded an activity.
    cache: 'no-store',
    
    headers: {
      'X-CSRF-TOKEN': csrf_token,
      'X-Requested-With': 'XMLHttpRequest',
    }
  }
}

export function fallbackDatepicker(input) {
  const test = document.createElement('input');
  const value = 'a';
  test.setAttribute('type', 'date');
  test.setAttribute('value', value);
  const isDateSupported = (test.value !== value);
  if (!isDateSupported) {
    // Fall back to jQuery datepicker on Safari browser.
    
    // Hide this input.
    input.setAttribute('hidden', '');
    
    // Create a new input for the bootstrap datepicker.
    const datepicker = document.createElement('input');
    input.parentNode.insertBefore(datepicker, input);
    
    const date = parseISO(input.value);
    
    // @ts-ignore
    $(datepicker).datepicker({
      autoclose: true,
      todayHighlight: true,
    })
    .datepicker('setDate', date).on('changeDate', (e)=>{
      
      // When datepicker date changes, modify the original
      // hidden input.
      input.value = format(e.date, 'yyyy-LL-dd');
      input.dispatchEvent(new Event('input'));
    });
  }
}

export function dispatchExerciseChangeEvent(element) {
  if (element) {
    const event = new CustomEvent('exercisechange', {
      bubbles: true
    });
    element.dispatchEvent(event);
  }
}

export function dispatchLikeChangeEvent(path, exerciseType, liked, disliked, target){
  const event = new CustomEvent('likechange', {
    bubbles: true,
    detail: {
      path,
      exerciseType,
      liked,
      disliked,
    }
  });
  target?.dispatchEvent(event);
}

export function dispatchReloadEvent(dates, target){
  let event = new CustomEvent("reload", {
    bubbles: true,
    detail: {
      dates,
    }
  });
  target?.dispatchEvent(event);
}

export function parseStorage(key) {
  const json: string = sessionStorage[key];
  if (json) try {
    return JSON.parse(json);
  } catch (e) {
    console.error(`Failed to sessionStorage[${key}].`);
  }
  return {};
}
export function saveStorage(storageKey, key, value) {
  const storage = parseStorage(storageKey);
  storage[key] = value;
  sessionStorage[storageKey] = JSON.stringify(storage);
}

export function customFlip(node: HTMLElement, {from, to}, params : any){
  //Used in RT and Workouts Dash when workout order changes due to a workout being liked
  //Scrolls to the new position of the workout only if it has moved up the list
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;
  const [ox, oy] = style.transformOrigin.split(' ').map(parseFloat);
  const dx = (from.left + from.width * ox / to.width) - (to.left + ox);
  const dy = (from.top + from.height * oy / to.height) - (to.top + oy);
  const d = Math.sqrt(dx * dx + dy * dy);
  const scrollsize = from.top - to.top;
  if(params.scroll){
    return {
      delay: 0,
      duration: Math.sqrt(d)*30,
      easing: quadInOut,
      css: (t, u) => {
        const x = u * dx;
        const y = u * dy;
        const sx = t + u * from.width / to.width;
        const sy = t + u * from.height / to.height;
        return `transform: ${transform} translate(${x}px, ${y}px) scale(${sx}, ${sy});`;
      },
      tick: (t, u) => {
        if(t === 1){
          if(Math.abs(scrollsize) > from.height*1.5 && scrollsize > 0 && node.getAttribute('data-ismoving') == "true"){
            node.scrollIntoView({block: "center", behavior: "smooth"});
          }
          node.removeAttribute('data-ismoving');
        }
      }
    }
  } else {
    return {
      delay: 0,
      duration: Math.sqrt(d)*40,
      easing: quadInOut,
      css: (t, u) => {
        const x = u * dx;
        const y = u * dy;
        const sx = t + u * from.width / to.width;
        const sy = t + u * from.height / to.height;
        return `transform: ${transform} translate(${x}px, ${y}px) scale(${sx}, ${sy});`;
      },
    }
  }
}

export function sanitize(unsafe) {
  const tmp = document.createElement('div');
  tmp.textContent = unsafe;
  return tmp.innerHTML;
  
}

export function addDays(date: Date | string, days: number): Date {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function formatFriendlyDate(inputDate: Date): string {
  const now = new Date();

  // Zero out time components for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateToCompare = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

  const diffInDays = Math.floor((dateToCompare.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  switch (diffInDays) {
    case 0:
      return 'Today';
    case -1:
      return 'Yesterday';
    case 1:
      return 'Tomorrow';
    default:
      return inputDate.toLocaleDateString(undefined, { weekday: "long", year: 'numeric', month: 'long', day: 'numeric' });
  }
}

export function formatFriendlyDatePossesive(inputDate: Date): string {
  const now = new Date();

  // Zero out time components for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateToCompare = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

  const diffInDays = Math.floor((dateToCompare.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  switch (diffInDays) {
    case 0:
      return "Today's";
    case -1:
      return "Yesterday's";
    case 1:
      return "Tomorrow's";
    default:
      return inputDate.toLocaleDateString(undefined, { weekday: "long", year: 'numeric', month: 'long', day: 'numeric' });
  }
}


export const noActivity = (daysToAdd: number, date=new Date()) => {
  return {
    start_date: addDays(date, daysToAdd),
    start_date_local: addDays(date, daysToAdd),
    state: ["none"],
    activityCount: 1
  }
}

export const activitiesCompleted = (daysToAdd: number, date=new Date()) => {
  return {
    start_date: addDays(date, daysToAdd),
    start_date_local: addDays(date, daysToAdd),
    state: ["completed"],
    activityCount: 1
  }
}

export enum XSSDeviationCategory {
  Good,
  Warn,
  Alert,
}

export type XSSDeviationDetails = {
  suitability: XSSDeviationCategory,
  score: number,
} 
export const xssDeviationToCategory = (values: XSSDetails, target: XSSDetails): XSSDetails<XSSDeviationDetails> => {
  
  // Borrowed from app/Progression.php > Progression::exerciseScore(...)
  const categorize = (xssV: number, target: number, denom: number, belowTargetMultiplier: number, baseRange: number) => {
    const score = baseRange * (belowTargetMultiplier * Math.max(0, target - xssV) / denom + Math.max(0, xssV - target) / denom)
    let suitability : XSSDeviationCategory;
    if (score > baseRange * 2) {
      suitability = XSSDeviationCategory.Alert
    } else if (score > baseRange) {
      suitability = XSSDeviationCategory.Warn
    } else {
      suitability = XSSDeviationCategory.Good
    }
    return {suitability, score};
  }
  
  // need to match the same calculations in Progression.php
  const lowDenom = Math.max(10, target.xlss / 4)
  const highDenom = Math.max(1.5, target.xhss / 1.2)
  const peakDenom = Math.max(0.5, target.xpss / 0.625)
  
  const xlss = categorize(Math.max(27, values.xlss), Math.max(27, target.xlss), lowDenom, 2, 100)
  const xhss = categorize(values.xhss, target.xhss, highDenom, 2, 100)
  const xpss = categorize(values.xpss, target.xpss, peakDenom, 4, 100)
  const xss = {suitability: Math.max(xlss.suitability, xpss.suitability, xhss.suitability), score:Math.max(xlss.score, xpss.score, xhss.score)};
  
  return {
    xss,
    xlss,
    xhss,
    xpss,
  }
}


export type XSSDetails<T=number> = {
  xss: T,
  xlss: T,
  xhss: T,
  xpss: T,
}

export const formatXSSDetails = (xssDetails: XSSDetails): XSSDetails<string> => {
  return {
    xss: Math.round(xssDetails.xss).toString(),
    xlss: Math.round(xssDetails.xlss).toString(),
    xpss: (Math.round(xssDetails.xpss * 10) / 10).toFixed(1),
    xhss: (Math.round(xssDetails.xhss * 10) / 10).toFixed(1)
  }
}

export const pDeviation = (val, og) => {
  if (og === 0) return Math.abs(val)
    return Math.abs(val - og) / og
}

export const specificityToRating = (specificity: number) => {
  if (specificity <= 1/3) return SpecificityTypes.Polar;
  else if (specificity >= 2/3) return SpecificityTypes.Pure;
  else return SpecificityTypes.Mixed;
}

//changes the weather icons that use a sun to use a moon if the time they are being displayed for is past the current sunset
export function changeToNightIcon(iconName, time, sunrise, sunset){
  //we do not care about adjusting the timezones here as we are simply comparing relative time
  //the backend returns all of these values in the same timezone (local time) so we are fine
  time = new Date(time*1000);
  sunrise = new Date(sunrise*1000);
  sunset = new Date(sunset*1000);
  if(sunrise.getHours() > time.getHours() || sunset.getHours() < time.getHours()){
    if(iconName == 'sun')
      return 'moon';
    if(iconName == 'sun-cloud')
      return 'moon-cloud';
  }
  return iconName;
}

export function formatRain(value){
  let displayUnits = Xert._displayUnits;
  let units;
  if(value === ""){
    return "-";
  } else {
    if(displayUnits == 'imperial'){
      units = 'in';
      value = (Math.round(value*10)/10).toFixed(1);
      if(value == '0.0'){
        value = '<0.1';
      }
    } else {
      value = Math.round(value);
      units = "mm";
      if(value == 0){
        value = "<1";
      }
    }
    return `${value}${units}`; 
  }
}
export function formatSnow(value){
  let displayUnits = Xert._displayUnits;
  let units;
  if(value === ""){
    return "-";
  } else {
    value = Math.round(value);
    if(displayUnits == 'imperial'){
      units = 'in';
    } else {
      units = "cm";
    }
    if(value == 0){
      value = '<1';
    }
    return `${value}${units}`;
  }
}

//OpenWeatherMapAPI returns weather forcasts in 3 hour increments, this finds the closest one, and returns null if the date is not within 3 hours of a forecast
export function findClosestWeather(forecasts, datetime){
  let datesArray = [];
  forecasts.forEach(forecast => {
    let date = makeDate(forecast.time);
    datesArray.push(date);
  });
  let closest = closestTo(datetime, datesArray);
  if(Math.abs(closest.getTime()-datetime.getTime())/(60*60*1000) > 3){//the closest date is not within three hours, so it is too far in the future
    return null;
  }
  let res = forecasts.find(forecast => {
    return makeDate(forecast.time).getTime() == closest.getTime();
  });
  return res;
}

export async function fetchTrainingAdvice(date, sport = null){
  let res = await (await window.fetch('/calendar/adaptive-training-advisor?' + new URLSearchParams({
    date: date.toISOString(),
    sport,
  }), {method: 'GET',
    ...getRequest()
  })).json();
  return res;
}

export function debouncer(timeout: number) {
  let timer;
  return {
    clear: ()=>{
      clearTimeout(timer);
    },
    debounce: (fn)=> {
      clearTimeout(timer);
      timer = setTimeout(fn, timeout);
    }
  }
}
export function throttle() {
  let promise;
  let nextFn;
  const throttle = (fn)=> {
    if (!promise) {
      nextFn = null;
      promise = fn().then(()=>{
        promise = null;
        if (nextFn) throttle(nextFn);
      })
    } else {
      nextFn = fn;
    }
  }
  const wait = async ()=> {
    while (promise) {
      await promise
    }
  }
  return {
    clear: ()=>{
      nextFn = null;
    },
    throttle,
    wait
  }
}

export function throttleTimeout<T extends (...args: any[]) => void>(fn: T, time: number) {
  let timer = null
  const trigger = async (...args: Parameters<T>) => {
    if (!timer) {
      timer = setTimeout(()=>{
        timer = null
        fn(...args)
      }, time)
    }
  }
  const clear = ()=>{
    if (timer) clearTimeout(timer)
      timer = null
  }
  return {
    trigger,
    clear
  }
}

export const convertSeconds = (seconds: number, includeHoursIfZero = true): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (includeHoursIfZero || hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

// A day the optimizer plans again when the forecast is adapted: a placeholder, or a workout the forecast scheduled that isn't pinned.
// Everything else (events, completed or manually scheduled activities, pinned workouts) stays as it is.
const isReplannedDay = (day: any): boolean =>
  !!(day.state?.includes('placeholder') || (day.state?.includes('forecast') && day.state?.includes('scheduled') && !day.state?.includes('pinned'))) &&
  !day.state?.includes('event_A') && !day.state?.includes('completed')

export const mergeForecastInputs = (data: any, defaults: CalendarDefaults, override_avail: OverrideAvailabilities, training_medians: any, updateTime = true): any => {
  const defaultsArray = [defaults.Sun, defaults.Mon, defaults.Tue, defaults.Wed, defaults.Thu, defaults.Fri, defaults.Sat]
  
  let daysByDateString = {}
  data.days.forEach((day, i) => {
    const date = fromUnixTime(day.t)
    const dayOfWeek = getDay(date) // Don't infer day from indices, use actual day of week, because you can have multiple activities per day
    const defaultDay = defaultsArray[dayOfWeek];
    
    // This is a bit messy. If default duration isn't specified, and this day's
    // duration is set (by TrainingForecastJob.php due to missing medians) use default.
    let duration = defaultDay.duration;
    if (duration == 999) duration = 5*60; // TODO: Remove once database fixed.
    if (defaultDay.duration <= 0 && day.duration > 0) duration = day.duration;
    // Set intensity explicitly: a missing key means 'advisor' and must not inherit a stale value from a stored forecast.
    let dayObj = { ...day, ...defaultDay, duration, intensity: defaultDay.intensity ?? 'advisor', 'dow': dayOfWeek, index: i };
    
    data.days[i] = dayObj;
    const dateString = LocalDate.format(date)
    if (daysByDateString[dateString] == null) daysByDateString[dateString] = [dayObj];
    else daysByDateString[dateString].push(dayObj);
  });
  
  if (override_avail) Object.entries(override_avail).forEach(([dateString, avail]) => {
    if (daysByDateString[dateString]) for(let day of daysByDateString[dateString]) {
      if (avail.duration == 999) avail.duration = 5*60; // TODO: Remove once database fixed.
      day = {
        ...day,
        ...avail,
        intensity: avail.intensity ?? 'advisor',
      }
      data.days[day.index] = day
    }
  })
  
  data.days.map(day => {
    day.availability = day.availability ? 1 : 0
    if (!day.availability || !day.intensity) day.intensity = 'advisor'
    day.duration = day.duration <= 0 ? -1 : day.duration
    day.training_category = day.training_category ? day.training_category : ''
    day.venue = day.venue ? day.venue : ''
    
    // Only update time for placeholder activities
    // TODO: Handle scheduled activities? Now scheduled activities are re-created as placeholders when running adapt.
    if (updateTime && (day.fixed !== true || (isReplannedDay(day) && !day.future)) && day.time) {
      let dayDate = startOfDay(fromUnixTime(day.t)) // date-fns start of day means start of day in local timezone
      
      if (day.time == 'advisor') {
        const dayOfWeek = getDay(dayDate)
        const dow = getDayMedians(training_medians, dayOfWeek)
        if (dow && dow['median_start_time']) {
          const time = dow['median_start_time']
          const hours = Math.floor(time)
          const minutes = Math.floor((time - hours)*60.0)
          dayDate.setHours(hours, minutes, 0)
        } else {
          console.warn('Missing medians for day', day)
        }
        
      } else {
        const timeString: string = day.time
        const hours = parseInt(timeString.slice(0, 2))
        const minutes = parseInt(timeString.slice(3, 5))
        
        dayDate.setHours(hours, minutes, 0)
      }
      
      
      day.t = getUnixTime(dayDate)
    }
    
    return day
  });
  // Sort since updateTime might have switched activities around.
  data.days.sort((a,b)=>a.t-b.t)
  return data
}

// trainingForecastInput argument is the response from CalendarController@getTrainingForecast, starting from today's dates
export const adaptForecast = async (prevForecastResults: any, trainingForecastInput: any, settings, defaults: CalendarDefaults, override_avail: OverrideAvailabilities, training_medians: any, messageCallback: (m) => void) => {
  const days = prevForecastResults.days as any[]
  // Unpinned workouts the forecast scheduled go back to the days they were chosen for. A workout may have more XSS than its
  // day (TrainingForecastJob::WORKOUT_XSS_TOLERANCE_ABOVE), and with the workouts the plan breaks its own recovery limits,
  // so a plan adapted as soon as it was made would be forecast again from scratch instead of kept.
  const plannedDays = new Map(days.filter(d => !d.state?.includes('event_A')).map(d => [d.t, d]))
  for (const day of trainingForecastInput.days) {
    const state = day.state ?? []
    if (!state.includes('forecast') || !state.includes('scheduled') || state.includes('pinned') || state.includes('event_A')) continue
    const planned = plannedDays.get(day.t)
    if (!planned) continue
    for (const key of ['xss', 'xlss', 'xhss', 'xpss', 'adu', 'sfd', 'sp']) if (planned[key] != null) day[key] = planned[key]
  }
  const mergedInput = mergeForecastInputs(trainingForecastInput, defaults, override_avail, training_medians)
  
  // The optimizer starts from the days as given, and a day the athlete has made unavailable still carrying the XSS the plan had
  // for it is a solution it can't improve on: it gives up on today ("No plan can reach your target") when that day is today.
  // A day it plans again and that isn't available is a rest day.
  for (const day of mergedInput.days) {
    if (day.availability || !isReplannedDay(day)) continue
    day.xss = day.xlss = day.xhss = day.xpss = 0
    day.adu = 0
  }
  
  console.log('adapt', {prevForecastResults, trainingForecastInput, mergedInput})
  
  // Use original training load values from previous forecast as targets to get back on track.
  let newDays = mergedInput.days as any[];
  for (let [i, j] = [newDays.length-1, days.length-1]; i >= 0 && j >= 0; [--i, --j]) {
    if (days[j].ltl_target) {
      // Use original training load target in case user got a breakthrough,
      // and the original focus power goal doesn't make sense anymore with the different signature.
      newDays[i].ltl_target = days[j].ltl_target;
      newDays[i].htl_target = days[j].htl_target;
      newDays[i].ptl_target = days[j].ptl_target;
    }
  }
  
  let forecastOptions = settings ? {
    ...defaultForecastOptions,
    recovery: settings.recovery,
    
    periodization: settings.periodization,
    
    highRestDays: settings.polarization,
    eventRestDays: settings.taper,
    
    maxWeeklyHours: settings.maxWeeklyHours,
    difficulty: settings.difficulty,
    difficultyFromTrainingLoad: settings.difficultyFromTrainingLoad,
    
    minimizeTraining: settings.minimizeTraining,
  } : defaultForecastOptions
  
  
  // console.log(trainingForecastInput, defaults, override_avail, mergedInput)
  // console.log(prevForecastResults.inputs, {...prevForecastResults.inputs, ...mergedInput})
  
  const worker = await (getWorker())
  return {
    result: await worker.training_forecast({...prevForecastResults.inputs, ...mergedInput, options: forecastOptions, original_days: days, keepPinned: true, getBackOnTrack: true, now: Math.floor(Date.now()/1000)}, messageCallback),
    inputs: mergedInput,
  }
}
export const mergeAdaptedForecast = (trainingPlan, inputs, forecastResults) => {
  // Merge new forecast with old forecast.
  // This way only future placeholders are updated, old ones are kept.
  
  const newDays = forecastResults['days']
  
  const t0 = Math.round(startOfDay(fromUnixTime(newDays[0]['t'])).getTime()/1000);
  // Keep old results/inputs from the past.
  const oldDays = trainingPlan.forecastResults.days.filter((day)=>day.t < t0)
  const oldInputs = trainingPlan.forecastResults.inputs.days.filter((day)=>day.t < t0)
  
  // Prepend old data from before today, to the new results that starts from today.
  forecastResults['days'] = [...oldDays, ...newDays];
  
  inputs.days = [...oldInputs, ...inputs.days]
  forecastResults.inputs = inputs
  
  // Adapted flag set so that server doesn't archive training plan and re-uses the same database object.
  forecastResults['adapted'] = true;
  forecastResults['keep_pinned'] = true;
}

/** Merges Default and Overidden availabilities, and then compares them to the settings the last forecast ran with. 
* @returns if availability changes require adapting the forecast.
*/
export const findModifiedDates = (lastForecastInputDays: any, defaults: CalendarDefaults, availabilities: OverrideAvailabilities): boolean => {
  // From the start of today: today's time of day has often passed, but Adapt Forecast still plans today.
  const startOfToday = startOfDay(new Date())
  let shouldAdaptAvail = false
  lastForecastInputDays
  .filter((day, i) => new Date(day.t*1000) >= startOfToday)
  .forEach((day, i) => {
    // Skip events, completed and pinned activities, etc. A workout the forecast scheduled is planned again by Adapt Forecast, so it counts.
    if (day.fixed && !isReplannedDay(day)) return
    const thisDate = fromUnixTime(day.t)
    const _forisodate = new Date(thisDate)
    _forisodate.setHours(12)
    _forisodate.setMinutes(0)
    _forisodate.setSeconds(0)
    const _date = _forisodate.toISOString().substring(0, 10);
    const weekday = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const default_settings = defaults[weekday[getDay(thisDate)]];
    const today_settings = availabilities[_date];
    const toCompare_settings = {...(today_settings ?? default_settings)} // copy to avoid modifying cal_settings
    if (toCompare_settings.duration <= 0) toCompare_settings.duration = -1
    if (day.duration <= 0) day.duration = -1
    // A Median day of a user without medians is forecast with the day's median_adu as its duration (see mergeForecastInputs),
    // so the plan stores that duration. It is still the Median the settings say, not a change.
    const dayDuration = toCompare_settings.duration <= 0 && day.duration > 0 && day.median_adu > 0 && day.duration === Math.round(day.median_adu*60) ? -1 : day.duration
    day.availability = !!day.availability
    // A day that is unavailable now and was when the forecast ran has nothing to adapt, whatever duration or time it carries.
    // (For a user with no history the server gives every day a default duration, unavailable ones too.)
    if (!toCompare_settings.availability && !day.availability) return
    // Increasing availability doesn't need adapt
    const availNeedsAdapt = (!toCompare_settings.availability && day.availability) ||
    (
      toCompare_settings.duration > 0 &&
      dayDuration > 0 &&
      toCompare_settings.duration < dayDuration
    ) ||
    (toCompare_settings.duration <= 0 && dayDuration > 0) ||
    (toCompare_settings.duration > 0 && dayDuration <= 0) ||
    (toCompare_settings.fixed_availability && !day.fixed_availability) || // Increasing flexibility doesn't need adapt
    toCompare_settings.time !== day.time ||
    // Any change of intensity needs an adapt. A missing value means 'advisor', and so does an unavailable day.
    (toCompare_settings.availability ? (toCompare_settings.intensity ?? 'advisor') : 'advisor') !== (day.availability ? (day.intensity ?? 'advisor') : 'advisor')
    // if
    if (availNeedsAdapt) {
      shouldAdaptAvail = true
    }
  })
  return shouldAdaptAvail
}

// NOTE: Is our activity expiration/completion logic solid?
export const isActivityCompleted = (activity) => (!activity.state && Date.parse(activity.start_date) < Date.now()) || activity.state?.includes('completed')

export const getForecastInputsForChart = async (tpForecastResults: any, defaults: CalendarDefaults, availabilities: OverrideAvailabilities, training_medians: any) => {
  const days = tpForecastResults.days as any[]
  const n = days.length
  
  const fromDate = new Date(days[0].t*1000)
  const toDate = new Date(days[n-1].t*1000)
  const trainingForecastInput = await  window.fetch(route('CalendarController@getTrainingForecast') + '?' + new URLSearchParams({
    fromDate: fromDate.toISOString(),
    toDate: toDate.toISOString(),
    duration: '-1',
    includePlaceholders: 'true', // Set flag to return previously scheduled placeholder activities, by default the forecast inputs skip those for generating a new forecast.
    includeWotdXss: 'true', // The chart recalculates the saved plan, so an upcoming workout of the day has to count its xss. Adapt Forecast doesn't ask for this.
  }), {
    ...getRequest(),
  }).then(r=>r.json())
  
  const mergedInput = mergeForecastInputs({...tpForecastResults.inputs, ...trainingForecastInput}, defaults, availabilities, training_medians, false)
  return mergedInput
}

/** Whether the saved forecast still has days breaking a constraint, judged from today's state (as Adapt Forecast does). */
const hasConflictsFromToday = async (tpForecastResults: any, defaults: CalendarDefaults, availabilities: OverrideAvailabilities, training_medians: any, forecastOptions: any): Promise<boolean> => {
  const days = tpForecastResults.days as any[]
  if (!days?.length) return false
  const toDate = new Date(days[days.length - 1].t*1000)
  const todayInput = await window.fetch(route('CalendarController@getTrainingForecast') + '?' + new URLSearchParams({
    toDate: toDate.toISOString(),
    duration: '-1',
    includePlaceholders: 'true',
    includeWotdXss: 'true',
  }), {
    ...getRequest(),
  }).then(r=>r.json())
  const mergedInput = mergeForecastInputs({...tpForecastResults.inputs, ...todayInput}, defaults, availabilities, training_medians, false)
  const results = (await getLibxert()).recalculate({...mergedInput, options: forecastOptions, keepPinned: true, getBackOnTrack: true})
  return !!results.days.find(d =>
    d.fixed !== true
    &&
    fromUnixTime(d.t) >= new Date()
    &&
    d.d_constraint > CONSTRAINT_EPSILON
  )
}

// The conflict check costs a request and a second recalculation, and the page's tabs ask for it at the same time with the same
// plan and settings: one run per plan object and settings object.
const conflictChecks = new WeakMap<object, WeakMap<object, Promise<boolean>>>()

/**
 * The saved forecast recalculated for the chart. Only a caller that shows whether the forecast is up to date (the red dot of
 * Adapt Forecast) passes checkConflicts: it then also gets isForecastUpToDate, else that is null.
 */
export const getForecastResultsForChart = async (tpForecastResults: any, tpSettings: any, cal_settings: CalendarSettings, training_medians: any, checkConflicts = false) => {
  const { defaults, availabilities } = cal_settings;
  
  const mergedInput = await getForecastInputsForChart(tpForecastResults, defaults, availabilities, training_medians)
  
  const settings = tpSettings
  const forecastOptions = settings ? {
    ...defaultForecastOptions,
    recovery: settings.recovery,
    
    periodization: settings.periodization,
    
    highRestDays: settings.polarization,
    eventRestDays: settings.taper,
    
    maxWeeklyHours: settings.maxWeeklyHours,
    difficulty: settings.difficulty,
    difficultyFromTrainingLoad: settings.difficultyFromTrainingLoad,
    
    minimizeTraining: settings.minimizeTraining,
  } : defaultForecastOptions
  
  const shouldAdaptAvail = findModifiedDates(tpForecastResults.inputs.days, defaults, availabilities)
  
  const forecastResults = (await getLibxert()).recalculate({...mergedInput, options: forecastOptions, keepPinned: true, getBackOnTrack: true})
  
  if (!checkConflicts) return {forecastResults, isForecastUpToDate: null as boolean | null}

  // The chart above simulates from the plan's first day, which drifts a little from the athlete's real state by now.
  // Placeholders sit right at their form limits, so that drift alone can flag a conflict the Adapt Forecast never saw.
  // Check conflicts the way Adapt Forecast runs: from today's state, with inputs from today to the end of the plan.
  let bySettings = conflictChecks.get(tpForecastResults)
  if (!bySettings) conflictChecks.set(tpForecastResults, bySettings = new WeakMap())
  let check = bySettings.get(cal_settings)
  if (!check) {
    // A request that fails says nothing about conflicts: not flagged.
    check = hasConflictsFromToday(tpForecastResults, defaults, availabilities, training_medians, forecastOptions).catch(() => false)
    bySettings.set(cal_settings, check)
  }
  // Concurrent callers share the request; once it has settled the entry goes, so a later call (the settings are changed in place) asks again.
  const hasConflictDays = await check.finally(() => { if (bySettings!.get(cal_settings) === check) bySettings!.delete(cal_settings) })

  const isForecastUpToDate: boolean | null = !shouldAdaptAvail && !hasConflictDays
  
  return {forecastResults, isForecastUpToDate}
}

export function maybeFade (node, params) {
  if (params.enableFade == true)
    return fade(node, params)
}

export function getRecommendChallengeLevel(recommendedDuration: number, magicIntervalData: any) {
  let closestCL = magicIntervalData[0].summary.cl
  let bestDifference = Infinity
  let difference = 0
  for(const mpaData of magicIntervalData) { // Select the lowest challenge level within 10% of the duration or closest to duration
      if (isRecommendChallengeLevel(recommendedDuration, mpaData.summary)) return mpaData.summary.cl
      difference = Math.abs(recommendedDuration - mpaData.summary.duration)
      if (bestDifference > difference) {
        closestCL = mpaData.summary.cl
        bestDifference = difference
      }
      
  }
  return closestCL
}

function isRecommendChallengeLevel(recommendedDuration: number, workoutSummary: any) {
  return workoutSummary.duration < recommendedDuration*1.1 && workoutSummary.duration > recommendedDuration*0.9
}

export async function getIntervalAdvice(type: ForecastType, recommendedDuration: number, signature: Signature, targets: XSSDetails, intervalMultiplier = 1.5, rampupMultiplier = 1.0) {
  if (type === ForecastType.Event) return null
  if (type !== ForecastType.HighIntensity) return {power: round(0.875*signature.ltp, 1)}
  const magicIntervalData = await generateMagicIntervals(signature, targets, intervalMultiplier, rampupMultiplier)
  const challengeLevel = getRecommendChallengeLevel(recommendedDuration, magicIntervalData)
  const intervalData = await generateMagicInterval(signature, targets, intervalMultiplier, rampupMultiplier, challengeLevel)
  return {
    power: round(intervalData.summary.intervalPower),
    duration: round(intervalData.summary.intervalDuration),
    recoveryPower: round(intervalData.summary.recoveryPower),
  }
}

export async function generateMagicIntervals(signature: Signature, targets: XSSDetails, intervalMultiplier = 1.5, rampupMultiplier = 1.0) {
  const libxert = await getLibxert()
  const out: {signature: Signature, summary: MagicBucketsSummary, intervals: IntervalData[]}[] = [];
  for (let cl = 2; cl < 10; cl += 1) {
    let data = libxert.magic_intervals(signature.pp, signature.ftp, signature.atc, targets.xlss, targets.xhss, targets.xpss, 0, cl, intervalMultiplier, rampupMultiplier, false)
    out.push({
      
      signature,
      summary: data.metrics,
      intervals: data.intervals,
    })
  }
  return out
}

export async function generateMagicInterval(signature: Signature, targets: XSSDetails, intervalMultiplier: number, rampupMultiplier: number, cl: number, doOutputData = true): Promise<MagicBucketsData> {
  const libxert = await getLibxert()
  const data = libxert.magic_intervals(signature.pp, signature.ftp, signature.atc, targets.xlss, targets.xhss, targets.xpss, 0, cl, intervalMultiplier, rampupMultiplier, doOutputData)
  const out: MagicBucketsRecord[] = []

  if (doOutputData) for (let i = 0; i < data.ts.length; ++i) {
    out.push({
      timestamp: data.ts[i]/1000.0,
      unix_time: data.ts[i],
      // mm: out.mms[i],
      mm: 0,
      power: data.ps[i],
      mpa: data.mpas[i],
      mmp0s: data.mmp0s[i],
      mmpMid: data.mmpMid[i],

      // tgf: out.fats[i],
      // tgc: out.carbs[i],

      // tws: out.tws[i],

      // lws: out.lws[i],
      // hws: out.hws[i],
      // pws: out.pws[i],

      // xds: out.xds[i],
      xssr: data.xssr[i],

      p_raw: data.ps[i],
      // hr: data.hr[k],
      // cad: data.cad[k],
      // spd: false ? (data.spd[k] > 8 ? 3600.0 / (data.spd[k] * distConversion) : undefined) : data.spd[k] * distConversion,
      // dist: data.dist[k] * distConversion,
      dist: 0,
      // alt: data.alt[k] * elevConversion,
      // lat: data.lat[k],
      // lng: data.lng[k],
      // tgt: data.tgt[k],
    });
  }

  return {
    records: out,
    signature,
    summary: data.metrics as MagicBucketsSummary,
    intervals: data.intervals as IntervalData[]
    // TODO: Do we need these anymore ??
      // result: true,
      // msg: '',
      // data: [],
      // // min: min,
      // events: [], // TODO
  };
}

// Global id counter for generating ids. Based on lodash.
// https://github.com/lodash/lodash/blob/main/src/uniqueId.ts
const idCounter = {}
export function uniqueId(prefix='component-') {
  if (!idCounter[prefix]) {
    idCounter[prefix] = 0
  }
  const id = ++idCounter[prefix]
  return `${prefix}${id}`
}

export function getDisplayWeight(weight: number, units: string) {
  if (units == 'lb') return Math.round(kgToLb(weight))
    return weight;
}

export function convertDisplayDistance(distKm: number, units: string) {
  if (units == 'mi') return Math.round(kmToMile(distKm))
    return Math.round(distKm);  
}

export function getDisplayAltitude(alt: number, units: string) {
  if(units == 'ft') return Math.round(meterToFoot(alt))
    return Math.round(alt)
}

export function getFunctionalWeight(weight: number, units: string) {
  if (units == 'lb') return lbToKg(weight)
    return weight
}

export function getLTP(ftp:number, atc:number) {
  return Math.max(0, ftp - atc / 400)
}

export type Units = {
  dist: string
  alt: string
  spd: string
}

export type LatLng = {
  lat: number
  lng: number
}

export function StrToProgramType(type:string, mode:string){
  if(!type) return ProgramType.Continuous;
  if(!mode) mode = "none"
  
  if(type == "targetEvent" &&  mode == "none") return ProgramType.BaseBuildPeak
  else if(type == "continuous" &&  mode == "none") return ProgramType.Continuous
  else if(type == "challenge" &&  mode == "none") return ProgramType.Challenge 
  else if(type == "targetEvent" &&  mode == "target_xss") return ProgramType.Event
  else if(type == "targetEvent" &&  mode == "target_focus") return ProgramType.Goal
  else if(type == "targetEvent" &&  mode == "target_race") return ProgramType.Race
  else return ProgramType.Continuous
}

export function fmFormatValue(num: number, perWeight: boolean) {
  var number = Number(num);
  if (perWeight) {
    return num.toFixed(1);
  } else {
    return Math.trunc(num);
  }
}

/** Start date input value ('yyyy-MM-dd') to plan.startDate. Today (or earlier) means start now, i.e. null. */
export const startDateFromInput = (value: string): Date | null => {
  if (!value) return null
  const date = LocalDate.atNoon(value)
  return date.getTime() <= endOfDay(new Date()).getTime() ? null : date
}

export const roundTo1Decimal = (num: number) => round(num, 1)

/* =========================================
Extract Info from Training Plan
============================================ */
const getTrainingPlanDate = (trainingPlan, trainingAdvice) => trainingPlan?.hasForecast ? LocalDate.atNoon(trainingPlan.date.substring(0, 10)) : new Date(trainingAdvice.target_event_timestamp * 1000)
// Start date chosen for a plan that hasn't started yet (Pre-Plan Maintenance Program). null = plan started/starts today.
const getTrainingPlanStartDate = (trainingPlan): Date | null => (trainingPlan?.is_pre_plan && trainingPlan?.planned_start_date) ? LocalDate.atNoon(LocalDate.format(new Date(trainingPlan.planned_start_date))) : null
// Same rule as User::hasForecastPlanOn(): forecast XSS targets apply while the plan is active, or during Pre-Plan
// Maintenance on/after the planned start date (XATA targets before it).
export const forecastTargetsApplyOn = (trainingPlan, date): boolean => !!trainingPlan?.isForecastPlan
  || (!!trainingPlan?.is_pre_plan && !!trainingPlan?.planned_start_date && date != null
    && new Date(date).getTime() >= new Date(trainingPlan.planned_start_date).getTime())
const getTrainingPlanFocus = (trainingPlan, userParams) => (trainingPlan?.focus && Object.keys(userParams.athletes).includes(trainingPlan?.focus)) ? trainingPlan?.focus : null
const getTrainingPlanSettings = (trainingPlan) => trainingPlan?.settings ? {...defaultTrainingPlanSettings, ...trainingPlan.settings} : null
export const createDeepCopy = (obj: Object) => {
  if (!!window.structuredClone) {
    return structuredClone(obj)
  } else {
    return JSON.parse(JSON.stringify(obj))
  }
}

export function createGoalPlan(trainingPlan, trainingAdvice, userParams, cal_settings: CalendarSettings): GoalPlan {
  return {
    program: ProgramType.Goal,
    startDate: getTrainingPlanStartDate(trainingPlan),
    targetDate: ((trainingPlan.program_type === "targetEvent" && trainingPlan.program_type !== "none") ? getTrainingPlanDate(trainingPlan, trainingAdvice) : defaultGoalPlan.targetDate) ?? defaultGoalPlan.targetDate,
    // Only a Focus Goal plan's own specificity carries over. Every plan has one, "Mixed" unless chosen, which is not a choice for a new Focus Goal.
    specificity: (trainingPlan?.selected_mode == "target_focus" ? trainingPlan?.specificity : null) ?? defaultGoalPlan.specificity,
    targetAthlete: getTrainingPlanFocus(trainingPlan, userParams) ?? defaultGoalPlan.targetAthlete,
    focusPower: trainingPlan?.focus_power ?? defaultGoalPlan.focusPower, 
    targetsMode: trainingPlan?.selected_mode == "target_focus" ? (trainingPlan?.goal_info?.targets_mode ?? defaultGoalPlan.targetsMode) : defaultGoalPlan.targetsMode,
    manualSignature: trainingPlan?.goal_info?.manual_signature ?? defaultGoalPlan.manualSignature,
    settings: getTrainingPlanSettings(trainingPlan) ?? defaultGoalPlan.settings,
    cal_settings: createDeepCopy(cal_settings),
  }
}

export function createEventPlan(trainingPlan, trainingAdvice, userParams, cal_settings: CalendarSettings): EventPlan {
  return {
    program: ProgramType.Event,
    startDate: getTrainingPlanStartDate(trainingPlan),
    targetDate: getTrainingPlanDate(trainingPlan, trainingAdvice) ?? defaultEventPlan.targetDate,
    specificity: trainingPlan?.specificity ?? defaultEventPlan.specificity,
    targetAthlete: getTrainingPlanFocus(trainingPlan, userParams) ?? defaultEventPlan.targetAthlete,
    xss: trainingPlan?.target_xss ?? defaultEventPlan.xss,
    name: (trainingPlan?.selected_mode == "target_xss" && trainingPlan?.name) ? trainingPlan?.name : defaultEventPlan.name,
    terrain: trainingPlan?.event_info?.terrain ?? defaultEventPlan.terrain,
    type: trainingPlan?.event_info?.type ?? defaultEventPlan.type,
    duration: trainingPlan?.event_info?.duration ?? defaultEventPlan.duration,
    targetsMode: trainingPlan?.selected_mode == "target_xss" ? (trainingPlan?.event_info?.targets_mode ?? defaultEventPlan.targetsMode) : defaultEventPlan.targetsMode,
    manualXss: trainingPlan?.event_info?.manual_xss ?? defaultEventPlan.manualXss,
    settings: getTrainingPlanSettings(trainingPlan) ?? defaultEventPlan.settings,
    cal_settings: createDeepCopy(cal_settings),
  }
}

export function createRacePlan(trainingPlan, trainingAdvice, userParams, cal_settings: CalendarSettings): RacePlan {
  return {
    program: ProgramType.Race,
    startDate: getTrainingPlanStartDate(trainingPlan),
    targetDate: getTrainingPlanDate(trainingPlan, trainingAdvice) ?? defaultRacePlan.targetDate,
    specificity: trainingPlan?.specificity ?? defaultRacePlan.specificity,
    targetAthlete: getTrainingPlanFocus(trainingPlan, userParams) ?? defaultRacePlan.targetAthlete,
    xss: trainingPlan?.target_xss ?? defaultRacePlan.xss,
    name: (trainingPlan?.selected_mode == "target_race" && trainingPlan?.name) ? trainingPlan?.name : defaultRacePlan.name,
    userTargetWeight: getDisplayWeight(trainingPlan?.race_info?.user_target_weight ?? defaultRacePlan.userTargetWeight, userParams.weight_unit),
    riderWeight: getDisplayWeight(trainingPlan?.race_info?.rider_weight ?? defaultRacePlan.riderWeight, userParams.weight_unit),
    carbsPerHour: trainingPlan?.race_info?.carbs_per_hour ?? defaultRacePlan.carbsPerHour,
    ignoreCarbs: trainingPlan?.race_info?.ignore_carbs ?? defaultRacePlan.ignoreCarbs,
    ignoreDifficulty: trainingPlan?.race_info?.ignore_difficulty ?? defaultRacePlan.ignoreDifficulty,
    difficultyScore: trainingPlan?.race_info?.difficulty_score ?? defaultRacePlan.difficultyScore,
    settings: getTrainingPlanSettings(trainingPlan) ?? defaultRacePlan.settings,
    cal_settings: createDeepCopy(cal_settings),
    fileData: null,
    activityPath: trainingPlan?.race_records?.source_activity_path,
    raceRecords: null,
    raceLogoUrl: null,
    raceSpeedScale: trainingPlan?.race_info?.speed_scale ?? defaultRacePlan.raceSpeedScale,
    racePowerScale: trainingPlan?.race_info?.power_scale ?? defaultRacePlan.racePowerScale,
  }
}

export function createContinuousPlan(trainingPlan, trainingAdvice, userParams, cal_settings: CalendarSettings): ContinuousPlan {
  return {
    targetAthlete: getTrainingPlanFocus(trainingPlan, userParams) ?? defaultContinuousPlan.targetAthlete,
    irValue: trainingAdvice?.training_gradient ?? defaultContinuousPlan.irValue,
    settings: getTrainingPlanSettings(trainingPlan) ?? defaultContinuousPlan.settings,
    cal_settings: createDeepCopy(cal_settings),
    specificity: undefined,
  }
}

export function createBbpPlan(trainingPlan, trainingAdvice, userParams, cal_settings: CalendarSettings): BbpPlan {
  return {
    targetDate: ((trainingPlan.program_type === "targetEvent" && trainingPlan.selected_mode === "none") ? getTrainingPlanDate(trainingPlan, trainingAdvice) : defaultBbpPlan.targetDate) ?? defaultBbpPlan.targetDate,
    specificity: trainingPlan?.specificity ?? defaultBbpPlan.specificity,
    targetAthlete: getTrainingPlanFocus(trainingPlan, userParams) ?? defaultBbpPlan.targetAthlete,
    irValue: trainingAdvice?.training_gradient ?? defaultBbpPlan.irValue,
    settings: getTrainingPlanSettings(trainingPlan) ?? defaultBbpPlan.settings,
    cal_settings: createDeepCopy(cal_settings),
  }
}

export function createChallengePlan(trainingPlan, trainingAdvice, userParams, cal_settings: CalendarSettings): ChallengePlan {
  return {
    targetDate: (trainingPlan.program_type === "challenge" ? getTrainingPlanDate(trainingPlan, trainingAdvice) : defaultChallengePlan.targetDate) ?? defaultChallengePlan.targetDate,
    targetAthlete: getTrainingPlanFocus(trainingPlan, userParams) ?? defaultChallengePlan.targetAthlete,
    irValue: trainingAdvice?.training_gradient ?? defaultChallengePlan.irValue,
    settings: getTrainingPlanSettings(trainingPlan) ?? defaultChallengePlan.settings,
    cal_settings: createDeepCopy(cal_settings),
    specificity: undefined,
  }
}

export function arrayFind(array: any[], accessors: string[], cmp: (a, b) => number) {
  const getFromArrayItem = (arrayItem) => accessors.reduce((acc, cur) => acc[cur], arrayItem)
  
  if (!array || array.length === 0) return null
  
  return array.reduce((acc, item) => {
    const itemValue = getFromArrayItem(item)
    return cmp(acc, itemValue) < 0 ? acc : itemValue
  }, getFromArrayItem(array[0]))
}

export function arrayFindMin(array: any[], accessors: string[]) {
  return arrayFind(array, accessors, (a, b) => a - b)
}

export function arrayFindMax(array: any[], accessors: string[]) {
  return arrayFind(array, accessors, (a, b) => b - a)
}

export function calcTsRating(totalTL: number) {
  const ts = totalTL
  if (ts >= 1 && ts < 25) {
    return ts/25*0.2
  } else if (ts >= 25 && ts < 50) {
    return 0.2 + (ts-25)/25*0.2
  } else if (ts >= 50 && ts < 75) {
    return 0.4 + (ts-50)/25*0.2
  } else if (ts >= 75 && ts < 110) {
    return 0.6 + (ts-75)/35*0.2
  } else if (ts >= 110 && ts < 150) {
    return Math.min(0.8 + (ts-110)/40*0.2,1)
  } else if (ts >= 150) {
    return 1
  } else {
    return 0
  }
}

export function getBaseBuildPeakPcnt(min: number, max:number) {
  const peakDays = 30
  const buildDays = 45
  const baseDays = 45
  const totDays = max-min
  
  const peak = (totDays-peakDays)/totDays*100
  const build = (totDays-peakDays-buildDays)/totDays*100
  const base = (totDays-peakDays-buildDays-baseDays)/totDays*100
  
  return {
    peak: Math.max(0, peak),
    build: Math.max(0, build),
    base: Math.max(0, base)
  }
}

const greyColor = '#b0b0b0'

/**
* @param data ramp rates 
* @param sliderSaturateToPcnt % of slider that should be colored in highly saturated RR colors
* @param thumbPcnt value% of the slider. The right side of the thumb will be either saturated or grayed out
* @param grey color to use for the grayed out part
* @param debug log internal values and input data
* 
* You can call with sliderSaturateToPcnt = thumbPcnt to get a slider that colors saturated upto a value, and gray otherwise (desaturation needs to be done seperately)
*/
export const getDateSliderColor = (data: number[], sliderSaturateToPcnt: number, thumbPcnt: number, grey=greyColor, debug=false) => {
  
  // This gradient works using two gradients and a mask
  // One gradient is a fully saturated color gradient (Ramp Rates) that goes all the way down the bar
  // Another gradient on top of it is a transparent/gray gradient that grays out incomplete days to the right of the thumb
  // Finally, transparent/low alpha mask desaturates projected ramp rates
  
  const brown = 'rgb(183,98,0)'
  const green = 'rgb(126,211,33)'
  const yellow = 'rgb(255,198,0)'
  const red = 'rgb(255,0,0)'
  
  const colours: string[] = [];
  
  data.forEach((rr) => {
    if(rr > 7) {
      colours.push(red);
    } else if (rr > 2) {
      colours.push(yellow);
    } else if (rr >= -1) {
      colours.push(green);
    } else {
      colours.push(brown);
    }
  });
  
  const remainingGrayFromPcnt = Math.max(sliderSaturateToPcnt, thumbPcnt)
  
  const gradArr = colours.map((colour, i) => {
    const thisColorPcnt = (i + 0.5)/colours.length*100;
    return `${colour} ${thisColorPcnt}%`
  })
  if (gradArr.length == 1) gradArr.push(gradArr[0])
    const grad = gradArr.join(', ')
  
  const grayGradientLayer = `linear-gradient(to right, transparent ${remainingGrayFromPcnt}%, ${grey} ${remainingGrayFromPcnt}%)`
  
  if (debug) console.log({data, colours, grad})
    
  if (colours.length > 0) {
    return `${grayGradientLayer}, linear-gradient(90deg, ${grad})`;
  } else {
    return `#A8A8A8`;
  }
}

export const getLHPGradient = (lhp: XSSDetails, max: number, alpha: number, remainingColor=greyColor) => {
  const pcntLTL = lhp.xlss / max * 100
  const pcntHTL = lhp.xhss / max * 100
  const pcntPTL = lhp.xpss / max * 100
  
  return `linear-gradient(to right, rgba(var(--rgb-xlss), ${alpha}) ${pcntLTL}%, rgba(var(--rgb-xhss), ${alpha}) ${pcntLTL}%, rgba(var(--rgb-xhss), ${alpha}) ${pcntLTL + pcntHTL}%, rgba(var(--rgb-xpss), ${alpha}) ${pcntLTL + pcntHTL}%, rgba(var(--rgb-xpss), ${alpha}) ${pcntLTL + pcntHTL + pcntPTL}%, ${remainingColor} ${pcntLTL + pcntHTL + pcntPTL}%)`
}

export async function projectUserFitness(
  currentSignature: XSSDetails, currentTL: XSSDetails, 
  ir_params: any, trainingGradient: number, specificity: number, sfd: number, 
  daysFromStart: number, daysProj: number, programType: string, difficulty: number
) {
  const libxert = await getLibxert()
  let projectedTL = libxert.project_training_loads(trainingGradient, currentTL, currentSignature, ir_params, specificity, sfd, daysFromStart, daysProj, programType, difficulty) as XSSDetails
  let projectedSignature = libxert.project_signature(currentSignature, currentTL, projectedTL, ir_params)
  let projectedFocusPower = libxert.power_from_duration(projectedSignature.xpss, projectedSignature.xhss, projectedSignature.xlss, sfd)
  
  return {
    tl: projectedTL,
    sig: projectedSignature,
    fp: projectedFocusPower
  }
}

export function scrollToElement(id: string, additionalVerticalOffset: number = 0) {
  const element = document.getElementById(id)
  const xertHeaderLength = 46
  const y = element?.getBoundingClientRect().top - xertHeaderLength + window.scrollY + additionalVerticalOffset
  window.scrollTo({top: y, behavior: 'smooth'})
}

// Shared by Messaging (ConversationPane) and Comments (CommentThreadPanel) "jump to" links --
// scrolls the target message/comment bubble into view, then briefly flashes it yellow and fades
// back out, so it's obvious which one was jumped to. Built on the Web Animations API: the
// animated background-color value outranks a plain style rule in the cascade (e.g. these rows'
// hover:bg-muted background), and once the animation finishes the element's rendered style
// reverts to normal on its own (default fill: 'none'), no color cleanup needed.
const JUMP_HIGHLIGHT_COLOR = 'rgba(250, 204, 21, 0.15)'
const JUMP_HIGHLIGHT_HOLD_MS = 100
const JUMP_HIGHLIGHT_FADE_MS = 2000
export function jumpToAndHighlight(elementId: string) {
  const el = document.getElementById(elementId)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const totalMs = JUMP_HIGHLIGHT_HOLD_MS + JUMP_HIGHLIGHT_FADE_MS
  const holdOffset = JUMP_HIGHLIGHT_HOLD_MS / totalMs

  el.animate(
    [
      { backgroundColor: JUMP_HIGHLIGHT_COLOR, offset: 0 },
      // Repeating the same color at the end of the hold fraction means the hold period renders as
      // a flat, unchanging color no matter what easing is applied to the animation as a whole --
      // only the offset(hold)->1 segment (the actual fade) is visibly eased.
      { backgroundColor: JUMP_HIGHLIGHT_COLOR, offset: holdOffset },
      { backgroundColor: 'transparent', offset: 1 },
    ],
    { duration: totalMs, easing: 'ease-in-out' }
  )
}

export async function getWeather(callback: (weatherForecast: any, hasSucceeded: boolean) => void): Promise<void> {
  let lat
  let lon
  if('geolocation' in navigator){
    navigator.geolocation.getCurrentPosition(async (position) => {
      lat = position.coords.latitude
      lon = position.coords.longitude
      const response = await window.fetch(('/calendar/weather/forecast?' + new URLSearchParams({
        lat,
        lon,
        cnt: '40',
      })), {method: 'GET',
        ...getRequest()})
        const result = await response.json()
        if (result?.['success'] === true) {
          callback(result, true)
        } else {
          callback(null, false)
        }
    })
  } else {
    callback(null, false)
  }  
}
  
export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export function switchMyFitnessTab(tabName: TabNames, element) {
  if (element) {
    const event = new CustomEvent('myfitnesstabchange', {
      bubbles: true,
      detail: {
        tabName
      }
    });
    element.dispatchEvent(event);
  }
}

// Medians for a day of the week (0 = Sunday): that day's own medians, else the weekday/weekend medians, else all days.
export const getDayMedians = (training_medians: any, dayOfWeek: number) => {
  const dayName = (dayOfWeek == 0 || dayOfWeek == 6) ? 'weekends' : 'weekdays'
  for (const medians of [training_medians['dow']?.[dayOfWeek]?.['all'], training_medians[dayName]?.['all']]) {
    if (medians && Object.entries(medians).length > 0) return medians
  }
  return training_medians['total']['all']
}

export const getDisplayMedians = (medians: any, date: Date = new Date()) => {
  let median_time, median_duration;
  median_time = medians['median_start_time'] ?? 12;
  median_duration = medians['median_adu'] ?? 1.0;
  
  // Updating the weather for the advised time
  const hours = Math.floor(median_time);
  const mins = Math.floor((median_time - hours) * 60);
  date.setHours(hours);
  date.setMinutes(mins);
  
  return {
    timedDate: date,
    duration: median_duration,
  }
}

/**
 * What 'the system chooses' comes to for a day of the week (0 = Sunday): its usual duration in minutes and start time ("HH:MM"),
 * from the medians, and whether the system makes it a high intensity day from the riding history (UserCalendarSettings::historyHighDays,
 * the setup wizard's rule: at least 8% of that weekday's XSS is high and peak XSS, or Tuesday and Thursday while there are too few rides).
 */
export const getUsualAvailability = (training_medians: any, dayOfWeek: number): { duration: number, time: string, high: boolean } | null => {
  if (!training_medians) return null
  let medians
  try { medians = getDayMedians(training_medians, dayOfWeek) } catch { return null }
  const { timedDate, duration } = getDisplayMedians(medians ?? {})
  return { duration: duration * 60, time: format(timedDate, 'HH:mm'), high: !!training_medians.high_intensity_days?.includes(dayOfWeek) }
}

export function signatureToXSSDetails(signature: Signature): XSSDetails {
  return {
    xss: null,
    xlss: signature.ftp,
    xhss: signature.atc,
    xpss: signature.pp,
  }
}

export function xssDetailsToSignature(details: XSSDetails): Signature {
  return {
    ftp: details.xlss,
    atc: details.xhss,
    pp: details.xpss,
    ltp: getLTP(details.xlss, details.xhss),
  }
}

export function getCurrentTrainingLoads(trainingAdvice): XSSDetails {
  return {
    xss: trainingAdvice.at_state.tl.ftp + trainingAdvice.at_state.tl.hie + trainingAdvice.at_state.tl.pp,
    xlss: trainingAdvice.at_state.tl.ftp,
    xhss: trainingAdvice.at_state.tl.hie,
    xpss: trainingAdvice.at_state.tl.pp,
  }
}

export function getCurrentFitnessSignature(trainingAdvice): XSSDetails {
  return signatureToXSSDetails(trainingAdvice.signature)
}

export function targetsLowIntensity(targets: XSSDetails) {
  return targets?.xhss == 0 || targets?.xpss == 0
}

export async function xssToFocusSpecificity(signature: Signature, xss: XSSDetails, athletes: any) {
  const libxert = await getLibxert()
  const specificity = libxert.get_specificity(signature.pp, signature.ftp, signature.atc, xss.xlss, xss.xhss, xss.xpss)
  const sfd = libxert.get_focus_duration(signature.pp, signature.ftp, signature.atc, xss.xlss, xss.xhss, xss.xpss)
  return {
    specificity,
    sfd,
    specificityRating: specificityToRating(specificity),
    focus: getFocusWorkoutName(athletes, sfd),

  }
}

export function getEnduranceDurationDifficulty(xlss: number) {
  return {
    duration: xlss/66*60,
    difficulty: xlss > 0 ? 66 : 0,
  }
}

export function xssDisplay(xss: XSSDetails<string>) {
  if (!xss) return '—';
  const total = xss.xss;
  const parts: string[] = [];
  if (xss.xlss != null) parts.push(`Low ${xss.xlss}`);
  if (xss.xhss != null) parts.push(`High ${xss.xhss}`);
  if (xss.xpss != null) parts.push(`Peak ${xss.xpss}`);
  if (total != null) parts.push(`Total ${total}`);
  return parts.join(' · ');
}

export function calculateCompletedXSS(activity: Exercise, matchedActivities: Exercise[]): XSSDetails<number> {
  if (!activity || !matchedActivities || matchedActivities.length == 0) return null
  
  return matchedActivities.reduce((acc: XSSDetails<number>, a) => {
    if (a.xss) {
      acc.xss += a.xss;
      if (a.xlss) acc.xlss += a.xlss;
      if (a.xhss) acc.xhss += a.xhss;
      if (a.xpss) acc.xpss += a.xpss;
    }
    return acc;
  }, { xss: 0, xlss: 0, xhss: 0, xpss: 0 });
}

export function handleImageError(ev: Event) {
  const target = ev.target as HTMLImageElement;
  target.src = Activity.getBackupImageUrl();
}

export function groupDuplicates(activities: Exercise[]): Map<string, Exercise[]> {
  const map = new Map<string, Exercise[]>();
  for (const a of activities) {
    if (!a.duplicate_group_id) continue;
    const key = a.duplicate_group_id;
    const list = map.get(key) || [];
    list.push(a);
    map.set(key, list);
  }
  return map;
}

export function capitalizeString(s: string) {
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}`
}

export async function copyStringToClipBoard(text: string) {
  const type = "text/plain"
  const clipboardItemData = {
    [type]: text,
  }
  const clipboardItem = new ClipboardItem(clipboardItemData)
  navigator.clipboard.write([clipboardItem]).then(() => Xert.success("Copy successful"), () => Xert.danger("Copy unsuccessful"))
}

export function onKeyboardEnter(event: KeyboardEvent, callback: () => void) {
  if(event.key === "Enter") callback()
}


/**
 * Waits for an event on the user's Pusher channel. Create it before making the request that causes the event, so the
 * event can't be missed. Resolves true when the event arrives, false after `timeoutMs`; cancel() stops waiting.
 * `accept`, given the event's data, picks out the event this caller is waiting for, when other requests (another tab,
 * an earlier save) send the same event.
 */
export function waitForUserEvent(event: string, timeoutMs = 10 * 60 * 1000, accept: (data: any) => boolean = () => true) {
  // @ts-ignore
  const channel = window.Xert?._userChannel
  let cancel = () => {}
  const promise = new Promise<boolean>(resolve => {
    let timeout: ReturnType<typeof setTimeout>
    const done = (arrived: boolean) => {
      clearTimeout(timeout)
      channel?.unbind(event, onEvent)
      resolve(arrived)
    }
    const onEvent = (data: any) => { if (accept(data)) done(true) }
    channel?.bind(event, onEvent)
    timeout = setTimeout(() => done(false), channel ? timeoutMs : 0)
    cancel = () => done(false)
  })
  return { promise, cancel }
}

/** The training advice and training plan as they are now, the same as the response of saving a training program. */
export function fetchTrainingAdvisor(): Promise<{ training_advice?: any, training_plan?: any }> {
  return window.fetch(route('UserController@getTrainingAdvisor'), getRequest()).then(r => r.json())
}

/** Event sent when workouts that a saved plan could not get straight away have been added (see GenerateForecastWorkoutsJob). */
export const FORECAST_WORKOUTS_COMPLETE_EVENT = 'forecast_workouts_complete'

/**
 * Starts waiting for the workouts of a plan save to be generated. Send `saveId` with the save as `save_id`: the plan
 * keeps it as its revision and FORECAST_WORKOUTS_COMPLETE_EVENT carries it back, so another save's event isn't taken
 * for this one's.
 */
export function waitForForecastWorkouts() {
  const saveId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return { saveId, ...waitForUserEvent(FORECAST_WORKOUTS_COMPLETE_EVENT, undefined, data => data?.revision === saveId) }
}


/** The range of weekly riding hours the account setup wizard offers, and what it starts at. */
export const SUGGESTED_WEEKLY_HOURS = { min: 4, max: 14, default: 6 } as const

/**
 * Minutes of riding on each day for a user with no training history, by whole hours a week (4 to 14). A day missing from a
 * pattern is unavailable. Short weeks keep a couple of rest days and put most of the riding at the weekend, so training
 * fits around a job; as the hours grow, rides get longer and spread onto the days left free (Fri, then Mon) instead of
 * only stretching the days already used. Each pattern adds up to its hours.
 */
const SUGGESTED_PATTERNS: Record<number, Partial<Record<ShortWeekDayProperties, number>>> = {
  4:  { Tue: 45, Thu: 45, Sat: 90, Sun: 60 },
  5:  { Tue: 45, Thu: 45, Sat: 120, Sun: 90 },
  6:  { Tue: 60, Wed: 60, Thu: 30, Sat: 120, Sun: 90 },
  7:  { Tue: 60, Wed: 45, Thu: 60, Sat: 150, Sun: 105 },
  8:  { Tue: 60, Wed: 60, Thu: 60, Sat: 180, Sun: 120 },
  9:  { Tue: 60, Wed: 60, Thu: 60, Fri: 30, Sat: 180, Sun: 150 },
  10: { Tue: 75, Wed: 60, Thu: 75, Fri: 30, Sat: 180, Sun: 180 },
  11: { Mon: 30, Tue: 60, Wed: 60, Thu: 60, Fri: 30, Sat: 240, Sun: 180 },
  12: { Mon: 30, Tue: 90, Wed: 60, Thu: 90, Fri: 30, Sat: 240, Sun: 180 },
  13: { Mon: 45, Tue: 90, Wed: 75, Thu: 90, Fri: 45, Sat: 240, Sun: 195 },
  14: { Mon: 45, Tue: 105, Wed: 90, Thu: 105, Fri: 45, Sat: 240, Sun: 210 },
}

/**
 * Hours a week of riding from which two days a week are high intensity rather than one, for a new user's starting
 * availabilities (here, and UserCalendarSettings::suggestDefaultsFromHistory for a user with riding history).
 */
export const TWO_HIGH_DAYS_FROM_HOURS = 8

/**
 * Starting availabilities for a user with no training history, from SUGGESTED_PATTERNS. Hours between two whole numbers use
 * the nearest pattern with its rides lengthened or shortened to the hours (in the 15 minute steps the duration pickers
 * offer, at least 30 minutes), and hours outside 4 to 14 are held to it. The days are flex, so a ride may run somewhat over
 * its time, and start at 9am (the browser's time, which the wizard saves as the user's timezone). Tuesday is high
 * intensity, and Thursday too from TWO_HIGH_DAYS_FROM_HOURS: Forecast AI then plans intervals on them, where a goal such as
 * 20 minute power would otherwise get next to none. Everything else about a day is kept.
 */
export function suggestAvailabilities(weeklyHours: number, defaults: CalendarDefaults): CalendarDefaults {
  const { min, max } = SUGGESTED_WEEKLY_HOURS
  const hours = Math.min(max, Math.max(min, weeklyHours))
  const patternHours = Math.round(hours)
  const pattern = SUGGESTED_PATTERNS[patternHours]
  const scale = hours / patternHours
  const suggested = {} as CalendarDefaults
  const highDays: ShortWeekDayProperties[] = hours >= TWO_HIGH_DAYS_FROM_HOURS ? ['Tue', 'Thu'] : ['Tue']
  for (const dow of Object.keys(defaults) as ShortWeekDayProperties[]) {
    const minutes = pattern[dow]
    suggested[dow] = minutes
      ? { ...defaults[dow], availability: true, duration: Math.max(30, Math.round(minutes * scale / 15) * 15), fixed_availability: false, time: '09:00',
          intensity: highDays.includes(dow) ? 'high' : 'advisor' }
      : { ...defaults[dow], availability: false, intensity: 'advisor' }
  }
  return suggested
}
